import React, { useEffect, useMemo, useState } from "react";
import { Button, Spinner } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  Server, Folder, Settings, Search, Container, Compass, Database,
  ShieldCheck, Globe, Command, Play,
} from "lucide-react";
import { fetchDeployApps, fetchProjectServers, getProjects } from "../redux/actionCreator";
import { project_data } from "../redux/commonReducer";
import DockerPanel from "./Docker/DockerPanel";
import K8sPanel from "./K8s/K8sPanel";
import NginxPanel from "./Nginx/NginxPanel";
import EnvPanel from "./Env/EnvPanel";
import DiscoverServicesModal from "./Discover/DiscoverServicesModal";
import MongoPanel from "./Mongo/MongoPanel";
import AuditPanel from "./Audit/AuditPanel";
import "./projectServersPage.scss";

/**
 * Full-page replacement for ProjectServersDialog. Lists a project's servers
 * with quick-action buttons (Terminal / Files / Docker / K8s / NGINX /
 * Subdomains). Reached at /projects/:projectId/servers.
 *
 * The Docker / K8s / NGINX panels are still modal-based (they're deep,
 * server-scoped tools that work well as overlays). Files, Terminal, and
 * Subdomains open in new tabs because they're standalone pages.
 */
export default function ProjectServersPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const projects = useSelector(project_data) || [];

  const [servers, setServers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const [dockerFor, setDockerFor] = useState(null);
  const [k8sFor, setK8sFor] = useState(null);
  const [nginxFor, setNginxFor] = useState(null);
  const [envFor, setEnvFor] = useState(null);
  const [discoverFor, setDiscoverFor] = useState(null);
  const [mongoFor, setMongoFor] = useState(null);
  const [auditFor, setAuditFor] = useState(null);
  const [projectApps, setProjectApps] = useState([]);

  // Find project in the redux list. If not present (deep link / page refresh),
  // fetch the list once so the title + project_name render.
  const project = useMemo(
    () => projects.find((p) => String(p._id) === String(projectId)) || null,
    [projects, projectId],
  );

  useEffect(() => {
    if (!project) dispatch(getProjects({ page: 0, limit: 100 }));
  }, [project, dispatch]);

  useEffect(() => {
    if (!projectId) return;
    setLoading(true);
    setErr(null);
    dispatch(fetchProjectServers(projectId)).then((res) => {
      setLoading(false);
      if (res?.status) setServers(res.data || []);
      else {
        setServers([]);
        setErr(res?.message || "Failed to load servers");
      }
    });
  }, [projectId, dispatch]);

  const openTerminal = (s) => {
    const qs = new URLSearchParams({
      label: s.label,
      host: `${s.username}@${s.host}:${s.port}`,
    });
    window.open(`/terminal/${projectId}/${s._id}?${qs.toString()}`, "_blank", "noopener,noreferrer");
  };

  const openFiles = (s) => {
    const qs = new URLSearchParams({
      label: `${project?.project_name || "Project"} · ${s.label}`,
    });
    window.open(`/files/${projectId}/${s._id}?${qs.toString()}`, "_blank", "noopener,noreferrer");
  };

  const openSubdomains = (s) => {
    const qs = new URLSearchParams({
      label: `${project?.project_name || "Project"} · ${s.label}`,
    });
    window.open(`/subdomains/${projectId}/${s._id}?${qs.toString()}`, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="project-servers-page">
      <header className="psp-head">
        <button type="button" className="psp-back" onClick={() => navigate("/projects")}>
          ‹ Projects
        </button>
        <div className="psp-title">
          <h2>Servers</h2>
          <div className="psp-sub text-muted">{project?.project_name || "—"}</div>
        </div>
      </header>

      {loading ? (
        <div className="psp-loading">
          <Spinner animation="border" />
        </div>
      ) : err ? (
        <div className="alert alert-danger">{err}</div>
      ) : servers.length === 0 ? (
        <div className="psp-empty">
          <div className="psp-empty-art"><Server size={40} strokeWidth={1.2} /></div>
          <h4>No servers configured</h4>
          <p>This project has no servers yet. Add one from the project editor.</p>
        </div>
      ) : (
        <div className="psp-table-wrap">
          <table className="psp-table">
            <thead>
              <tr>
                <th>Label</th>
                <th>Host</th>
                <th>User</th>
                <th>Auth</th>
                <th className="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {servers.map((s) => (
                <tr key={s._id}>
                  <td className="psp-label">{s.label}</td>
                  <td>
                    <code>{s.host}:{s.port}</code>
                  </td>
                  <td>{s.username}</td>
                  <td>
                    <span className="badge bg-secondary">
                      {s.auth_type === "key" ? "Key" : "Password"}
                    </span>
                  </td>
                  <td className="text-end psp-actions">
                    {s.has_files && (
                      <Button size="sm" variant="light" onClick={() => openFiles(s)} title="Open the file manager">
                        <Folder size={14} className="me-1" /> Files
                      </Button>
                    )}
                    <Button size="sm" variant="light" onClick={() => setEnvFor(s)} title="Setup & health — detect/install Docker & Kubernetes (k3s)">
                      <Settings size={14} className="me-1" /> Setup
                    </Button>
                    <Button size="sm" variant="success" onClick={() => setDiscoverFor(s)} title="Discover existing pm2 / Docker / k8s / nginx services on this server and import them as deploy apps">
                      <Search size={14} className="me-1" /> Discover
                    </Button>
                    <Button size="sm" variant="light" onClick={() => setDockerFor(s)} title="Manage Docker containers">
                      <Container size={14} className="me-1" /> Docker
                    </Button>
                    <Button size="sm" variant="light" onClick={() => setK8sFor(s)} title="Manage Kubernetes pods">
                      <Compass size={14} className="me-1" /> K8s
                    </Button>
                    {s.mongo_enabled && (
                      <Button size="sm" variant="light" onClick={() => setMongoFor(s)} title="MongoDB — browse, backup, restore">
                        <Database size={14} className="me-1" /> Mongo
                      </Button>
                    )}
                    <Button
                      size="sm" variant="light"
                      title="Post-deploy security audit + per-app code-quality (npm audit / outdated)"
                      onClick={async () => {
                        setAuditFor(s);
                        // Lazy-load apps the first time so the "App quality" tab has data.
                        if (!projectApps.length) {
                          const r = await dispatch(fetchDeployApps(projectId));
                          if (r?.status) setProjectApps(r.data?.apps || []);
                        }
                      }}
                    >
                      <ShieldCheck size={14} className="me-1" /> Audit
                    </Button>
                    {s.nginx_enabled && (
                      <>
                        <Button size="sm" variant="light" onClick={() => openSubdomains(s)} title="Manage subdomains">
                          <Globe size={14} className="me-1" /> Subdomains
                        </Button>
                        <Button size="sm" variant="light" onClick={() => setNginxFor(s)} title="Edit raw nginx configs">
                          <Command size={14} className="me-1" /> NGINX
                        </Button>
                      </>
                    )}
                    <Button size="sm" variant="primary" onClick={() => openTerminal(s)} title="Open terminal in a new tab">
                      <Play size={14} className="me-1" /> Terminal
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <DockerPanel
        show={!!dockerFor}
        onHide={() => setDockerFor(null)}
        project={project || { _id: projectId }}
        server={dockerFor || {}}
      />
      <K8sPanel
        show={!!k8sFor}
        onHide={() => setK8sFor(null)}
        project={project || { _id: projectId }}
        server={k8sFor || {}}
      />
      <NginxPanel
        show={!!nginxFor}
        onHide={() => setNginxFor(null)}
        project={project || { _id: projectId }}
        server={nginxFor || {}}
      />
      <EnvPanel
        show={!!envFor}
        onHide={() => setEnvFor(null)}
        project={project || { _id: projectId }}
        server={envFor || {}}
      />
      <DiscoverServicesModal
        show={!!discoverFor}
        onHide={() => setDiscoverFor(null)}
        projectId={projectId}
        server={discoverFor || {}}
      />
      <MongoPanel
        show={!!mongoFor}
        onHide={() => setMongoFor(null)}
        project={project || { _id: projectId }}
        server={mongoFor || {}}
      />
      <AuditPanel
        show={!!auditFor}
        onHide={() => setAuditFor(null)}
        project={project || { _id: projectId }}
        server={auditFor || {}}
        apps={projectApps}
      />
    </div>
  );
}
